import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import pinPrompt from '../utils/pinPrompt';
import { useMobileMode } from '../mobile/MobileModeContext.jsx';
import FindProgress from '../components/FindProgress.jsx';
import { currentActor, findBeaconSaying } from '../utils/beacons';
import { STATUS_LABELS, queueStatusLabel } from '../features/salesOrders/palletActions';
import {
  checkedSentence, identityChips, lastCheckLine, loadPalletCheck, makeRequestId, nounOf, okPayload, orderLine, palletHeadline, recordPalletCheck,
} from '../features/palletCheck/palletCheckData';
import { Alerts, Banner } from '../features/palletCheck/PalletBanner.jsx';
import PackingSection from '../features/palletCheck/PackingPicture.jsx';
import { BoxLightbox, StackLightbox } from '../features/palletCheck/PackingLightbox.jsx';
import PartsList, { PartLightbox } from '../features/palletCheck/PartsList.jsx';
import ProblemForm from '../features/palletCheck/ProblemForm.jsx';
import Overlay from '../features/palletCheck/Overlay.jsx';
import HoldingMoveDialog from '../components/HoldingMoveDialog.jsx';
import { C, FONT, chip, card, linkButton, sectionTitle } from '../features/palletCheck/styles';

/**
 * /pallet-check/:palletId — the screen a sales-order pallet beacon's QR opens (2026-10-23). Anyone on the
 * floor checks the pallet in front of them against what JobQ expects: who it is for, the banner (the same
 * decision as the printed label and the beacon screen), how it should be packed, the parts with pictures,
 * what happens next, and two buttons: "Pallet matches: record check" and "Something's wrong".
 * Not admin-gated (like /s/:code). Phone first (inside the phone shell when the phone app is on), a single
 * centred column on a desktop. API: jobq-api docs/pallet-check-contract.md.
 */

const STEP_DOT = {
  done: { background: C.green, color: '#FFFFFF' },
  current: { background: C.amber, color: '#1F1300' },
  todo: { background: C.track, color: C.sub },
};

function NextSteps({ steps }) {
  if (!steps || !steps.length) return null;
  return (
    <div style={{ padding: '24px 16px 0', display: 'flex', flexDirection: 'column', gap: 10 }}>
      <h2 style={sectionTitle}>What happens next</h2>
      <ol style={{ ...card, listStyle: 'none', margin: 0, display: 'flex', flexDirection: 'column', gap: 10, fontSize: 15 }} data-testid="next-steps">
        {steps.map((s, i) => (
          <li key={`${i}-${s.label}`} style={{ display: 'flex', gap: 10, alignItems: 'center' }} data-state={s.state}>
            <span aria-label={s.state === 'done' ? 'done' : s.state === 'current' ? 'now' : 'later'}
              style={{ width: 24, height: 24, borderRadius: 999, fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, ...(STEP_DOT[s.state] || STEP_DOT.todo) }}>
              {s.state === 'done' ? '✓' : i + 1}
            </span>
            <span style={{ fontWeight: s.state === 'current' ? 700 : 400 }}>{s.label}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

/** "Kit boxes": each kit and what goes in it (people count cartons against this screen). */
function KitBoxes({ kits, noun }) {
  const list = Array.isArray(kits) ? kits : [];
  if (!list.length) return null;
  return (
    <div style={{ padding: '24px 16px 0', display: 'flex', flexDirection: 'column', gap: 10 }} data-testid="kit-boxes">
      <h2 style={sectionTitle}>Kit boxes on this {noun} ({list.length})</h2>
      {list.map((k) => {
        const items = Array.isArray(k.items) ? k.items : [];
        const count = items.reduce((n, i) => n + (Number(i.quantity) || 0), 0);
        return (
          <div key={k.kit_box_id} style={{ ...card, display: 'flex', flexDirection: 'column', gap: 8 }} data-testid={`kit-box-${k.kit_box_id}`}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 700, fontSize: 16, flexGrow: 1 }}>{k.label || 'Kit box'}{k.kind ? ` · ${k.kind}` : ''}</span>
              <span style={{ fontSize: 14 }}>{count} item{count === 1 ? '' : 's'}</span>
              {k.status && <span style={{ fontSize: 13, fontWeight: 700, color: k.status === 'Sealed' ? '#14532D' : C.sub }}>{k.status}</span>}
            </div>
            {items.length > 0 ? (
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 15 }}>
                {items.map((i, n) => <li key={`${i.product_id}-${n}`}><b>{i.quantity} ×</b> {i.product_id}{i.description ? ` · ${i.description}` : ''}</li>)}
              </ul>
            ) : <div style={{ fontSize: 14, color: C.sub }}>Nothing listed in this kit.</div>}
          </div>
        );
      })}
    </div>
  );
}

export default function PalletCheckPage() {
  const { palletId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { phoneLayoutFor } = useMobileMode();
  const inPhoneShell = phoneLayoutFor(location.pathname);

  const [state, setState] = useState({ phase: 'loading' });
  const [overlay, setOverlay] = useState(null); // {kind: 'stack'|'box'|'part'|'problem'|'photo', ...}
  const [checkMsg, setCheckMsg] = useState(null); // {bad, text}
  const [checking, setChecking] = useState(false);
  const [labelMsg, setLabelMsg] = useState(null);
  const [labelBusy, setLabelBusy] = useState(false);
  const [find, setFind] = useState(null);
  const [findMsg, setFindMsg] = useState(null);
  // "Pallet matches": one client_request_id per check, sent again when the person retries after an error
  // or a timeout (the API answers `duplicate: true` for one it recorded); a fresh one after it went through.
  const okRequestId = useRef(null);

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setState({ phase: 'loading' });
    const r = await loadPalletCheck(palletId);
    if (r.ok && r.body) setState({ phase: 'ready', data: r.body });
    else setState((cur) => (quiet && cur.data ? cur : { phase: 'error', message: r.message, retry: r.status === 0 || r.status >= 500 }));
  }, [palletId]);

  useEffect(() => { load(); }, [load]);

  const data = state.data;
  const pallet = data ? data.pallet : null;
  const order = data ? data.order : null;

  const { noun, Noun } = nounOf(data);

  const recordOk = async () => {
    setCheckMsg(null);
    // Always asked (never the admin session's PIN: a device left unlocked must not record a check in the
    // admin's name); a face at a station says who looked.
    const pin = await pinPrompt(`Your PIN, to record that ${(pallet && pallet.short_id) || `this ${noun}`} matches:`, { autofill: false, face: true });
    if (!pin) return;
    if (!okRequestId.current) okRequestId.current = makeRequestId();
    setChecking(true);
    const r = await recordPalletCheck(palletId, okPayload(pin, okRequestId.current));
    setChecking(false);
    if (!r.ok) { setCheckMsg({ bad: true, text: r.message }); return; }
    okRequestId.current = null; // `duplicate: true` is a success too: the check is on record
    setCheckMsg({ bad: false, text: checkedSentence(r.body && r.body.check) });
    load({ quiet: true });
  };

  const onProblemSent = (body) => {
    setOverlay(null);
    const posted = body && body.slack_posted === false ? ' (the shop alert didn’t post: tell the office)' : '';
    setCheckMsg({ bad: false, text: `Sent to the office${posted}. The ${noun} stays flagged until someone clears it.` });
    load({ quiet: true });
  };

  const printLabel = async () => {
    const def = STATUS_LABELS.pallet(pallet.pallet_id);
    const r = await queueStatusLabel({ ...def, idLabel: pallet.short_id, trigger: 'pallet_check', setBusy: setLabelBusy, setMessage: () => {} });
    setLabelMsg({ bad: !r.done, text: r.message });
  };

  const findBeacon = async () => {
    setFindMsg(null);
    const said = await findBeaconSaying(data.beacon.beacon_id, currentActor());
    if (said.find) setFind(said.find);
    else setFindMsg({ bad: said.tone === 'danger', text: said.text });
  };

  const backLabel = order && order.order_number ? `Back to ${order.order_number}` : 'Back';
  const header = inPhoneShell ? null : (
    <div style={{ background: C.ink, color: '#FFFFFF', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
      <button type="button" aria-label={backLabel} onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))}
        style={{ color: '#FFFFFF', background: 'none', border: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 44, height: 44, margin: '-10px 0 -10px -10px', cursor: 'pointer' }}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 18l-6-6 6-6" /></svg>
      </button>
      <div style={{ fontSize: 17, fontWeight: 600, flexGrow: 1 }}>{Noun} check</div>
      <div style={{ fontSize: 13, color: C.dim }}>JobQ</div>
    </div>
  );

  const shell = (children) => (
    <div data-testid="pallet-check" style={{ background: C.paper, fontFamily: FONT, color: C.ink, minHeight: inPhoneShell ? undefined : '100vh' }}>
      <div style={{ maxWidth: 520, margin: '0 auto', background: C.paper, display: 'flex', flexDirection: 'column', minHeight: inPhoneShell ? undefined : '100vh' }}>
        {header}
        {children}
      </div>
    </div>
  );

  if (state.phase === 'loading') {
    return shell(
      <div role="status" style={{ padding: 32, textAlign: 'center', color: C.muted }}>
        <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />Looking up the pallet…
      </div>,
    );
  }
  if (state.phase === 'error') {
    return shell(
      <div style={{ padding: 16 }}>
        <div role="alert" style={{ ...card, fontSize: 16 }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Pallet check</div>
          <div>{state.message}</div>
          {state.retry && (
            <button type="button" onClick={() => load()} style={{ marginTop: 12, minHeight: 48, borderRadius: 10, border: 0, background: C.ink, color: '#FFFFFF', padding: '0 16px', fontFamily: 'inherit', fontSize: 16, fontWeight: 700, cursor: 'pointer' }}>
              Try again
            </button>
          )}
        </div>
      </div>,
    );
  }

  const chips = identityChips(data);
  const openProblems = data.open_problems || [];

  return shell(
    <>
      <div style={{ padding: '18px 16px 6px', display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ fontSize: 13, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: C.muted }}>You scanned</div>
        <h1 style={{ fontFamily: C.mono, fontSize: 46, fontWeight: 700, lineHeight: 1, margin: 0, overflowWrap: 'anywhere' }} data-testid="pallet-short-id">{pallet.short_id}</h1>
        <div style={{ fontSize: 18, fontWeight: 600 }}>{palletHeadline(pallet, order)}</div>
        {orderLine(order) && <div style={{ fontSize: 15, color: C.sub }}>{orderLine(order)}</div>}
        {chips.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
            {chips.map((c) => <span key={c} style={chip}>{c}</span>)}
          </div>
        )}
      </div>

      <Banner banner={data.banner} />
      <Alerts palletId={palletId} problems={openProblems} warnings={data.warnings}
        onCleared={() => { setCheckMsg({ bad: false, text: 'Problem cleared.' }); load({ quiet: true }); }}
        onPhoto={(url) => setOverlay({ kind: 'photo', url })} />

      <PackingSection data={data}
        onOpenStack={(container, zone, view = null) => setOverlay({ kind: 'stack', container, zone, view })}
        onOpenBox={(container) => setOverlay({ kind: 'box', container })} />

      <KitBoxes kits={data.kit_boxes} noun={noun} />

      <PartsList parts={data.parts} noun={noun} onOpen={(part) => setOverlay({ kind: 'part', part })} />

      <NextSteps steps={data.next_steps} />

      <div style={{ padding: '26px 16px 0', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <button type="button" onClick={recordOk} disabled={checking}
          style={{ minHeight: 56, border: 0, borderRadius: 12, background: C.green, color: '#FFFFFF', fontFamily: 'inherit', fontSize: 18, fontWeight: 700, cursor: 'pointer' }}>
          {checking ? 'Recording…' : `${Noun} matches: record check`}
        </button>
        <button type="button" onClick={() => { setCheckMsg(null); setOverlay({ kind: 'problem' }); }}
          style={{ minHeight: 56, border: `2px solid ${C.red}`, borderRadius: 12, background: '#FFFFFF', color: C.red, fontFamily: 'inherit', fontSize: 18, fontWeight: 700, cursor: 'pointer' }}>
          Something's wrong
        </button>
        {checkMsg && (
          <div role={checkMsg.bad ? 'alert' : 'status'} data-testid="check-message"
            style={{ fontSize: 16, fontWeight: 700, textAlign: 'center', color: checkMsg.bad ? C.red : C.green }}>{checkMsg.text}</div>
        )}
        <div style={{ fontSize: 13, color: C.sub, textAlign: 'center' }} data-testid="last-check">
          Recording a check asks for your PIN. {lastCheckLine(data.last_check)}
        </div>
      </div>

      <div style={{ padding: '22px 16px 28px', display: 'flex', flexWrap: 'wrap', gap: '4px 16px', alignItems: 'center', fontSize: 15, fontWeight: 600 }}>
        {data.links && data.links.sales_order_path && (
          <Link to={data.links.sales_order_path} style={{ color: C.blue, minHeight: 44, display: 'inline-flex', alignItems: 'center' }}>
            Open {order && order.order_number ? order.order_number : 'the sales order'}
          </Link>
        )}
        <button type="button" onClick={printLabel} disabled={labelBusy} style={linkButton}>{labelBusy ? 'Printing…' : 'Print status label'}</button>
        {data.beacon && data.beacon.beacon_id && (
          <button type="button" onClick={findBeacon} style={linkButton}>Find the beacon (blink)</button>
        )}
        {pallet.wip_task_id != null && pallet.status === 'Building' && (
          // a holding pallet: combine it with another of the order's holding pallets or split it (2026-10-29)
          <button type="button" onClick={() => setOverlay({ kind: 'move' })} style={linkButton}
            data-testid="pallet-check-move">Move pieces</button>
        )}
      </div>
      {(labelMsg || findMsg || find) && (
        <div style={{ padding: '0 16px 28px', display: 'flex', flexDirection: 'column', gap: 6, fontSize: 14 }}>
          {labelMsg && <div role="status" style={{ color: labelMsg.bad ? C.red : C.sub }}>{labelMsg.text}</div>}
          {findMsg && <div role="status" style={{ color: findMsg.bad ? C.red : C.sub }}>{findMsg.text}</div>}
          {find && <FindProgress find={find} beaconId={data.beacon && data.beacon.beacon_id} onRetry={findBeacon} />}
        </div>
      )}

      <StackLightbox open={!!overlay && overlay.kind === 'stack'} onClose={() => setOverlay(null)} data={data}
        container={overlay && overlay.container} zone={overlay && overlay.zone} initialView={overlay && overlay.view} />
      <BoxLightbox open={!!overlay && overlay.kind === 'box'} onClose={() => setOverlay(null)} data={data} container={overlay && overlay.container} />
      <PartLightbox open={!!overlay && overlay.kind === 'part'} onClose={() => setOverlay(null)} part={overlay && overlay.part}
        palletShortId={pallet.short_id} noun={noun} />
      <ProblemForm open={!!overlay && overlay.kind === 'problem'} onClose={() => setOverlay(null)} palletId={palletId} noun={noun}
        palletShortId={pallet.short_id} orderNumber={order && order.order_number}
        expectedUnits={data.counts ? data.counts.units_planned : null} onSent={onProblemSent} />
      {overlay && overlay.kind === 'move' && (
        <HoldingMoveDialog show palletId={palletId} onClose={() => setOverlay(null)} onMoved={() => load({ quiet: true })} />
      )}
      <Overlay open={!!overlay && overlay.kind === 'photo'} onClose={() => setOverlay(null)} title="Problem photo" testId="photo-lightbox">
        {overlay && overlay.url && (
          <div style={{ padding: 16, display: 'flex', justifyContent: 'center' }}>
            <img src={overlay.url} alt="What was reported, as sent to the office" style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: 6 }} />
          </div>
        )}
      </Overlay>
    </>,
  );
}
